// CelebrateSection.tsx — saves encrypted lesson to lessons_learned table with accordion and checkmark
import React, { useState, useEffect, useRef } from 'react';
import { supabase } from '../lib/supabase';
import { Button } from './Button';
import { useEncryption } from '../hooks/useEncryption';
import { CheckSquare, ChevronDown, CheckCircle2, BookOpen } from 'lucide-react';
import { startOfDay, endOfDay } from 'date-fns';
import { cn } from '../utils/cn';
import { useNavigate } from 'react-router-dom';

export default function CelebrateSection() {
  const [open, setOpen] = useState(false);
  const [lesson, setLesson] = useState('');
  const [isSaving, setIsSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [errorMessage, setErrorMessage] = useState('');
  const [timestamp, setTimestamp] = useState(Date.now());
  const [hasCompletedToday, setHasCompletedToday] = useState(false);
  const [userTimeZone, setUserTimeZone] = useState('');
  const { encrypt } = useEncryption();
  const wakeLockRef = useRef<WakeLockSentinel | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    const interval = setInterval(() => {
      setTimestamp(Date.now());
    }, 15000);
    return () => clearInterval(interval);
  }, []);

  useEffect(() => {
    async function fetchUserTimeZone() {
      try {
        const { data: { session } } = await supabase.auth.getSession();
        if (!session?.user?.id) return;

        const { data: profile } = await supabase
          .from('profiles')
          .select('time_zone')
          .eq('id', session.user.id)
          .single();

        if (profile?.time_zone) {
          setUserTimeZone(profile.time_zone);
        } else {
          // Default to browser's timezone if not set in profile
          setUserTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
        }
      } catch (error) {
        console.error('Error fetching user timezone:', error);
        // Fall back to browser's timezone
        setUserTimeZone(Intl.DateTimeFormat().resolvedOptions().timeZone);
      }
    }

    fetchUserTimeZone();
  }, []);

  useEffect(() => {
    const fetchCompletionStatus = async () => {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) return;
      
      // Get today's date in user's timezone
      const today = new Date();
      const { data } = await supabase
        .from('growth_days')
        .select('lesson_learned')
        .eq('user_id', session.user.id)
        .gte('date', startOfDay(today).toISOString())
        .lte('date', endOfDay(today).toISOString())
        .maybeSingle();

      if (data?.lesson_learned) {
        setHasCompletedToday(true);
      }
    };

    fetchCompletionStatus();
  }, [userTimeZone]);

  const handleSave = async () => {
    setIsSaving(true);
    setSuccessMessage('');
    setErrorMessage('');

    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session?.user) throw new Error('Not logged in');

      const encryptedLesson = await encrypt(lesson);
      const today = startOfDay(new Date()).toISOString();

      const { error } = await supabase.from('lessons_learned').insert([
        {
          user_id: session.user.id,
          date: today,
          lesson: encryptedLesson
        }
      ]);

      if (error) throw error;

      await supabase.from('growth_days').upsert([
        {
          user_id: session.user.id,
          date: today.slice(0, 10),
          lesson_learned: true,
          updated_at: new Date().toISOString()
        }
      ], { onConflict: ['user_id', 'date'] });

     setSuccessMessage('🎉 Lesson saved successfully!');
     setTimeout(() => setSuccessMessage(''), 5000);

      setLesson('');
      setHasCompletedToday(true);
    } catch (err: any) {
      console.error('Save error:', err);
      setErrorMessage(err.message || 'Error saving lesson');
    } finally {
      setIsSaving(false);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    setLesson(e.target.value);
    if ('wakeLock' in navigator && wakeLockRef.current === null && e.target.value.trim()) {
      (navigator as any).wakeLock.request('screen')
        .then((lock: WakeLockSentinel) => {
          wakeLockRef.current = lock;
        })
        .catch(console.warn);
    }
  };

return ( 
  <div className="border rounded-xl overflow-hidden bg-white shadow-lg">
    <button
      onClick={() => setOpen(prev => !prev)}
      className="w-full flex justify-between items-center px-4 py-6 text-left hover:bg-blue-50"
    >
      <div className="flex items-center gap-2 text-[#004060] font-semibold text-xl">
        <BookOpen className="w-6 h-6 text-[#236caf]" />
        Lessons
      </div>
      <ChevronDown
        className={cn(
          'w-5 h-5 text-[#236caf] transition-transform duration-200',
          open && 'rotate-180'
        )}
      />
    </button>

    {open && (
      <div className="p-4 sm:p-6 space-y-4 text-sm text-gray-800">
        {/* Success Message */}
        {successMessage && (
          <div className="text-green-600 font-medium bg-green-50 border border-green-300 p-2 rounded-md">
            {successMessage}
          </div>
        )}

        {/* Error Message */}
        {errorMessage && (
          <div className="text-red-600 font-medium bg-red-50 border border-red-300 p-2 rounded-md">
            {errorMessage}
          </div>
        )}

        {/* Textarea Input */}
        <textarea
          value={lesson}
          onChange={handleInputChange}
          className="w-full p-4 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#236caf] focus:border-transparent resize-none h-32"
          placeholder="Write your insight, lesson, or takeaway here. Works best by dictating on your phone."
        />

        {/* Action Buttons */}
        <div className="flex gap-4">
          <Button
            onClick={handleSave}
            disabled={!lesson.trim() || isSaving}
            variant="outline"
            className="w-full"
          >
            {isSaving ? 'Saving...' : 'Save Lesson'}
          </Button>
          <Button
            variant="outline"
            onClick={() => navigate('/lessons-history')}
            className="w-full"
          >
            History
          </Button>
        </div>
      </div>
    )}

    <div style={{ display: 'none' }} data-timestamp={timestamp}></div>
  </div>
);
}